import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { MessageSquare, Plus, Trash2, RefreshCw, Search, Users, GraduationCap, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Panel de gestión de Foros de Clase para Administradores.
 * Permite crear/eliminar el foro privado de cada clase.
 * Los estudiantes inscriptos + profesor son automáticamente miembros del foro.
 */
const GestionForosClases = () => {
  const { API, user } = useContext(AuthContext);
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [search, setSearch] = useState('');
  // Por defecto mostramos solo clases ACTIVAS — son las que tienen alumnos
  // cursando hoy. El admin puede cambiar el filtro para ver todas.
  const [filtroEstado, setFiltroEstado] = useState('activas-todas'); // activas-todas | activas-con-foro | activas-sin-foro | todas

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadClases = async () => {
    try {
      const res = await axios.get(`${API}/admin/clases-con-foros`, { headers: headers() });
      const lista = res.data?.clases || [];
      // Orden alfabético por título (case-insensitive, soporta acentos)
      lista.sort((a, b) =>
        (a.titulo || '').localeCompare(b.titulo || '', 'es', { sensitivity: 'base' })
      );
      setClases(lista);
    } catch (e) {
      toast.error('Error cargando clases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.rol !== 'admin') return;
    loadClases();
  }, [user]);

  const handleCrearForo = async (clase) => {
    setActionId(clase.id);
    try {
      const res = await axios.post(`${API}/admin/clases/${clase.id}/foro/crear`, {}, { headers: headers() });
      if (res.data?.already_exists) {
        toast.info('El foro ya existía');
      } else {
        toast.success(`Foro creado para ${clase.titulo}`);
      }
      loadClases();
    } catch (e) {
      toast.error('Error creando foro');
    } finally {
      setActionId(null);
    }
  };

  const handleEliminarForo = async (clase) => {
    setActionId(clase.id);
    try {
      await axios.delete(`${API}/admin/clases/${clase.id}/foro`, { headers: headers() });
      toast.success(`Foro deshabilitado para ${clase.titulo}`);
      loadClases();
    } catch (e) {
      toast.error('Error deshabilitando foro');
    } finally {
      setActionId(null);
    }
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="p-8 text-center text-gray-500">
        Solo administradores pueden gestionar foros de clase.
      </div>
    );
  }

  const clasesFiltradas = clases.filter(c => {
    if (search && !(c.titulo || '').toLowerCase().includes(search.toLowerCase())) return false;
    // Por defecto solo activas; "todas" muestra también las inactivas
    if (filtroEstado !== 'todas' && !c.activa) return false;
    if (filtroEstado === 'activas-con-foro' && !c.forum_enabled) return false;
    if (filtroEstado === 'activas-sin-foro' && c.forum_enabled) return false;
    return true;
  });

  const stats = {
    total: clases.length,
    activas: clases.filter(c => c.activa).length,
    conForo: clases.filter(c => c.forum_enabled && c.activa).length,
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="gestion-foros-clases">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-indigo-600" />
            Foros de Clase
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Activá un foro privado para cada clase. Los inscritos + profesor lo verán automáticamente.
          </p>
        </div>
        <Button onClick={loadClases} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" /> Refrescar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-lg p-3 border shadow-sm text-center">
          <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-[11px] text-gray-500 uppercase">Total clases</div>
        </div>
        <div className="bg-white rounded-lg p-3 border shadow-sm text-center">
          <div className="text-2xl font-bold text-emerald-600">{stats.activas}</div>
          <div className="text-[11px] text-gray-500 uppercase">Activas</div>
        </div>
        <div className="bg-white rounded-lg p-3 border shadow-sm text-center">
          <div className="text-2xl font-bold text-indigo-600">{stats.conForo}</div>
          <div className="text-[11px] text-gray-500 uppercase">Con foro</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar clase..."
            className="pl-9"
            data-testid="foros-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'activas-todas', label: '🟢 Activas' },
            { value: 'activas-con-foro', label: '✅ Activas c/foro' },
            { value: 'activas-sin-foro', label: '⚪ Activas s/foro' },
            { value: 'todas', label: 'Todas (incl. inactivas)' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFiltroEstado(f.value)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition ${
                filtroEstado === f.value
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de clases */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />)}
        </div>
      ) : clasesFiltradas.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">
          No hay clases que coincidan con tu búsqueda.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {clasesFiltradas.map(clase => (
            <Card key={clase.id} className={`border-l-4 ${
              !clase.activa
                ? 'border-l-gray-300 opacity-70'
                : clase.forum_enabled
                  ? 'border-l-indigo-500'
                  : 'border-l-emerald-400'
            }`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-800 truncate">{clase.titulo}</h3>
                      {clase.forum_enabled && (
                        <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                          <MessageSquare className="h-3 w-3 mr-1" /> Foro activo
                        </Badge>
                      )}
                      {!clase.activa && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
                          🕐 Inactiva
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <GraduationCap className="h-3 w-3" /> {clase.profesor_nombre || 'Sin profesor'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" /> {clase.estudiantes_count} inscritos
                      </span>
                      {clase.semestre && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> Semestre {clase.semestre}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 sm:flex-shrink-0">
                    {clase.forum_enabled ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                            disabled={actionId === clase.id}
                            data-testid={`btn-eliminar-foro-${clase.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Deshabilitar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Deshabilitar foro?</AlertDialogTitle>
                            <AlertDialogDescription>
                              El foro de <strong>{clase.titulo}</strong> dejará de estar disponible
                              para los estudiantes y el profesor. Los posts existentes se mantienen
                              en histórico, pero nadie podrá ver ni publicar.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleEliminarForo(clase)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Deshabilitar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleCrearForo(clase)}
                        disabled={actionId === clase.id}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid={`btn-crear-foro-${clase.id}`}
                      >
                        {actionId === clase.id
                          ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />Creando...</>
                          : <><Plus className="h-3.5 w-3.5 mr-1.5" />Crear foro</>
                        }
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default GestionForosClases;
